import { Outlet, Link, useLocation } from 'react-router-dom';
import { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useAuth } from '../hooks/useAuth';
import { usePendingTaskCount } from '../hooks/useTasks';
import { NotificationsPanel } from './NotificationsPanel';
import {
  DocumentTextIcon,
  BuildingOfficeIcon,
  RectangleStackIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  BellIcon,
  Bars3Icon,
} from '@heroicons/react/24/outline';

export function Layout() {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const [showNotifications, setShowNotifications] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { count: notificationCount } = usePendingTaskCount();

  // Close transient UI (notifications panel, mobile drawer) on route change.
  useEffect(() => {
    setShowNotifications(false);
    setMobileNavOpen(false);
  }, [location.pathname]);

  const userName =
    user?.user_metadata?.name ||
    user?.user_metadata?.full_name ||
    `${user?.user_metadata?.given_name || ''} ${user?.user_metadata?.family_name || ''}`.trim() ||
    user?.email?.split('@')[0] ||
    'User';

  const navigation = [
    { name: 'Contracts', href: '/contracts', icon: DocumentTextIcon },
    { name: 'Clients', href: '/clients', icon: BuildingOfficeIcon },
    { name: 'Templates', href: '/templates', icon: RectangleStackIcon },
    { name: 'Settings', href: '/settings', icon: Cog6ToothIcon },
  ];

  // Shared sidebar body — rendered both inside the persistent desktop
  // aside and inside the mobile slide-over drawer.
  const sidebarContent = (
    <div className="flex h-full flex-col bg-white">
      <div className="flex items-center h-16 px-4 border-b border-gray-200">
        <Link to="/" className="flex items-center gap-2">
          <img
            src="/brand/privion-logo-on-light.svg"
            alt="Privion"
            className="h-7 select-none"
            draggable={false}
          />
          <span className="text-sm font-medium text-gray-500 border-l border-gray-300 pl-2">
            CLM
          </span>
        </Link>
      </div>

      <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
        {navigation.map((item) => {
          const isActive = location.pathname.startsWith(item.href);
          const Icon = item.icon;
          return (
            <Link
              key={item.name}
              to={item.href}
              className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Icon className="w-5 h-5 shrink-0" />
              <span>{item.name}</span>
            </Link>
          );
        })}
      </nav>

      <div className="border-t border-gray-200 p-3">
        <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{userName}</p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
          </div>
          <button
            onClick={signOut}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md shrink-0"
            title="Sign out"
          >
            <ArrowRightOnRectangleIcon className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Persistent left sidebar — desktop (lg+) only */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-30 lg:flex lg:w-64 lg:flex-col border-r border-gray-200">
        {sidebarContent}
      </aside>

      {/* Mobile slide-over drawer */}
      <Transition.Root show={mobileNavOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setMobileNavOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/60" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-200 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-150 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative flex w-72 max-w-[85%] flex-1">
                {sidebarContent}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Right of the sidebar: top bar + scrollable main */}
      <div className="lg:pl-64">
        {/* Top bar — hamburger + mobile logo + notifications. On desktop
            it's just notifications (sidebar carries the logo + nav). */}
        <div className="sticky top-0 z-20 flex h-16 items-center gap-2 bg-white border-b border-gray-200 px-3 sm:px-4">
          <button
            onClick={() => setMobileNavOpen(true)}
            className="lg:hidden p-2 -ml-1 text-gray-600 hover:bg-gray-100 rounded-md"
            aria-label="Open navigation"
          >
            <Bars3Icon className="w-6 h-6" />
          </button>

          <Link to="/" className="lg:hidden flex items-center gap-2">
            <img
              src="/brand/privion-logo-on-light.svg"
              alt="Privion"
              className="h-6 select-none"
              draggable={false}
            />
            <span className="text-xs font-medium text-gray-500 border-l border-gray-300 pl-2">
              CLM
            </span>
          </Link>

          <div className="flex-1" />

          <div className="relative">
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Notifications"
            >
              <BellIcon className="w-6 h-6" />
              {notificationCount > 0 && (
                <span className="absolute top-1 right-1 flex items-center justify-center w-4 h-4 text-[10px] font-bold text-white bg-red-500 rounded-full border-2 border-white">
                  {notificationCount > 9 ? '9+' : notificationCount}
                </span>
              )}
            </button>

            <NotificationsPanel
              isOpen={showNotifications}
              onClose={() => setShowNotifications(false)}
            />
          </div>
        </div>

        <main>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
